import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";

import { useCoursesOptions } from "api/hooks/useCourses";
import { useSmsDefaultSender } from "api/hooks/useSmsDefaultSender";
import {
  useCreateScheduledMessage,
  useScheduledMessage,
  useUpdateScheduledMessage,
} from "api/hooks/useScheduledMessages";
import {
  CreateScheduledMessageRequest,
  UpdateScheduledMessageRequest,
} from "api/scheduledmessages/scheduledmessagesApi";
import { PaziaBox, PaziaBreadcrumbs } from "components/atoms";
import { FormSection } from "components/FormSection";
import { PageMain } from "components/molecules";
import { routes } from "consts";
import { formatDate } from "utils";
import {
  PaziaButton,
  PaziaInput,
  PaziaLoader,
  PaziaSelect,
} from "components/atoms";
import { useToaster } from "utils";
import { MessageRecipientSelector } from "./MessageRecipientSelector";
import { SmsEditor } from "./SmsEditor";

const createSchema = () =>
  yup.object().shape({
    title: yup.string().required("Tytuł jest wymagany").max(255, "Tytuł może mieć maksymalnie 255 znaków"),
    subject: yup.string().notRequired(),
    content: yup.string().required("Treść jest wymagana").max(320, "SMS może mieć maksymalnie 320 znaków"),
    recipients: yup
      .array()
      .min(1, "Wybierz co najmniej jednego odbiorcę")
      .max(1000, "Maksymalnie 1000 odbiorców")
      .required("Odbiorcy są wymagani"),
    selectedTemplate: yup.string().optional(),
    courseId: yup.number().optional(),
    sender: yup.string().required("Nadawca jest wymagany"),
  });

const SMS_SENDER_OPTIONS = ["SzkoleniaGW", "PAZiA"];

interface ScheduledMessageFormProps {
  isEdit?: boolean;
}

export const ScheduledMessageForm = ({ isEdit = false }: ScheduledMessageFormProps) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToaster();

  // Create dynamic schema based on message type
  const schema = createSchema();

  // Per-tenant default sender (the "sms-default-sender" setting), "SzkoleniaGW" until it loads or if unset.
  const { defaultSender, isLoading: isLoadingDefaultSender } = useSmsDefaultSender();
  const senderOptions = (
    SMS_SENDER_OPTIONS.includes(defaultSender) ? SMS_SENDER_OPTIONS : [defaultSender, ...SMS_SENDER_OPTIONS]
  ).map((sender) => ({ value: sender, label: sender }));

  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: "",
      subject: "",
      content: "",
      recipients: [],
      selectedTemplate: "",
      courseId: undefined,
      sender: defaultSender,
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
    resetField,
    control,
  } = form;

  // API hooks
  const { scheduledMessage, isLoading: isLoadingMessage } = useScheduledMessage(isEdit ? Number(id) : null);
  const createMutation = useCreateScheduledMessage();
  const updateMutation = useUpdateScheduledMessage();
  const { options: coursesOptions } = useCoursesOptions();

  // Watch selected course
  const selectedCourseId = useWatch({ control, name: "courseId" });
  const selectedCourse = coursesOptions?.find((opt) => opt.value === selectedCourseId?.toString())?.course;

  // useForm captured its defaults before the setting may have loaded, so apply it to a new message
  // once it is known (the form is not rendered until then).
  useEffect(() => {
    if (!isEdit && !isLoadingDefaultSender) {
      resetField("sender", { defaultValue: defaultSender });
    }
  }, [isEdit, isLoadingDefaultSender, defaultSender, resetField]);

  // Load existing message data for editing
  useEffect(() => {
    if (isEdit && scheduledMessage) {
      reset({
        title: scheduledMessage.title,
        subject: scheduledMessage.subject || "",
        content: scheduledMessage.content,
        recipients: [], // Will be populated by MessageRecipientSelector
        courseId: scheduledMessage.course?.id,
        sender: scheduledMessage.sender || defaultSender,
      });
    }
  }, [isEdit, scheduledMessage, reset, defaultSender]);

  const onSubmit = async (data: any) => {
    try {
      let content = data.content;
      let jsonBody = "";

      const messageData: CreateScheduledMessageRequest | UpdateScheduledMessageRequest = {
        title: data.title,
        content: content,
        jsonBody: jsonBody,
        courseId: data.courseId,
        recipients: data.recipients,
        sender: data.sender,
      };

      if (isEdit && id) {
        await updateMutation.mutateAsync({
          id: Number(id),
          data: messageData as UpdateScheduledMessageRequest,
        });
        showSuccess("Wiadomość została zaktualizowana");
      } else {
        const response = await createMutation.mutateAsync(messageData as CreateScheduledMessageRequest);
        showSuccess("Wiadomość została utworzona");
        navigate(`${routes.EMAILS}${routes.SCHEDULED_MESSAGES}${response.data.id}`);
      }
    } catch (error) {
      showError("Wystąpił błąd podczas zapisywania wiadomości");
    }
  };

  if ((isEdit && isLoadingMessage) || isLoadingDefaultSender) {
    return (
      <PageMain>
        <PaziaLoader />
      </PageMain>
    );
  }

  return (
    <div>
      <PaziaBreadcrumbs
        className="mb-2"
        segments={[
          { title: "SMS", path: routes.EMAILS + routes.SCHEDULED_MESSAGES },
          { title: isEdit ? "Edytuj" : "Utwórz" },
        ]}
      />
      <PaziaBox className="mt-2">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="mt-2 space-y-6">
            <FormSection number={1} title={isEdit ? "Edytuj zaplanowany SMS" : "Utwórz zaplanowany SMS"} color="orange">
              <PaziaInput name="title" placeholder="Wprowadź nazwę wysyłki..." required />
            </FormSection>

            <FormSection number={2} title="Odbiorcy" color="blue">
              <MessageRecipientSelector name="recipients" error={errors.recipients?.message} />
            </FormSection>

            <FormSection number={3} title="Kontekst" color="purple">
              <div className="space-y-4">
                <PaziaSelect
                  name="courseId"
                  label="Kurs (opcjonalnie)"
                  options={coursesOptions}
                  placeholder="Wybierz kurs..."
                />

                {/* Placeholder information */}
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    <div className="mb-1 font-medium">Dostępne zmienne:</div>
                    <div className="space-y-1 text-xs">
                      {/* Course-related placeholders */}
                      <div className="flex items-center">
                        <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{"{{courseName}}"}</code>
                        <span className="ml-2">- nazwa kursu</span>
                        {selectedCourse && (
                          <span className="pl-2 font-medium text-blue-600 dark:text-blue-400">
                            {selectedCourse.name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center">
                        <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{"{{courseStartDate}}"}</code>
                        <span className="ml-2">- data rozpoczęcia</span>
                        {selectedCourse && (
                          <span className="pl-2 font-medium text-blue-600 dark:text-blue-400">
                            {formatDate(selectedCourse.dateOfCourse)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center">
                        <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{"{{courseEndDate}}"}</code>
                        <span className="ml-2">- data zakończenia</span>
                        {selectedCourse && (
                          <span className="pl-2 font-medium text-blue-600 dark:text-blue-400">
                            {formatDate(selectedCourse.endOfCourse)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center">
                        <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{"{{courseEdition}}"}</code>
                        <span className="ml-2">- edycja kursu</span>
                        {selectedCourse && (
                          <span className="pl-2 font-medium text-blue-600 dark:text-blue-400">
                            {selectedCourse.edition}
                          </span>
                        )}
                      </div>
                      {/* User-related placeholders */}
                      <div className="flex items-center">
                        <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{"{{userEmail}}"}</code>
                        <span className="ml-2">- email użytkownika</span>
                      </div>
                      <div className="flex items-center">
                        <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{"{{userName}}"}</code>
                        <span className="ml-2">- pełne imię i nazwisko użytkownika</span>
                      </div>
                      <div className="flex items-center">
                        <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{"{{userFirstName}}"}</code>
                        <span className="ml-2">- imię użytkownika</span>
                      </div>
                      <div className="flex items-center">
                        <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">{"{{userSurname}}"}</code>
                        <span className="ml-2">- nazwisko użytkownika</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </FormSection>

            {/* Content Editor */}
            <FormSection number={4} title="Treść wiadomości" color="green">
              <div className="space-y-4">
                <PaziaSelect name="sender" label="Nadawca *" options={senderOptions} placeholder="Wybierz nadawcę..." />
                <SmsEditor name="content" error={errors.content?.message} />
              </div>
            </FormSection>

            {/* Actions */}
            <div className="flex justify-end space-x-4">
              <PaziaButton type="secondary" onClick={() => navigate("/scheduled-messages")}>
                Anuluj
              </PaziaButton>
              <PaziaButton
                type="primary"
                processing={createMutation.isPending || updateMutation.isPending}
                btnType="submit"
              >
                {isEdit ? "Zaktualizuj wiadomość" : "Utwórz wiadomość"}
              </PaziaButton>
            </div>
          </form>
        </FormProvider>
      </PaziaBox>
    </div>
  );
};
